import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { KpiTimeCycleChartData } from '@models';
import { PortfolioMilestoneFilterService } from '@services';
import { Subject, takeUntil } from 'rxjs';

type PortfolioKpiExpandedViewType = 'chart' | 'table';
interface MilestoneStatusSummaryItem {
  label: string;
  value: number;
}
interface KpiRemoveFilterPayload {
  field: string;
  value: string;
}
const DEFAULT_TIME_CYCLE_CHART_DATA: KpiTimeCycleChartData = {
  identifier: 'timeCycleChart',
  title: 'Average cycle time',
  tooltipText:
    'Cycle time is the elapsed time from the first milestone’s start to the second milestone’s completion.',
  labels: [],
  offset: [],
  timeWeeks: [],
  category: [],
  cycleCount: 0
};

@Component({
  selector: 'kpi-expanded-view',
  templateUrl: './kpi-expanded-view.component.html',
  styleUrl: './kpi-expanded-view.component.scss'
})
export class KpiExpandedViewComponent implements OnInit, OnDestroy {
  dialogRef = inject(MatDialogRef<KpiExpandedViewComponent>);
  private milestoneFilterService = inject(PortfolioMilestoneFilterService);
  private destroy$ = new Subject<void>();
  private loadingSyncTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly initialLoadingSyncDelayMs = 75;
  private readonly maxLoadingSyncDelayMs = 500;
  data: {
    title: string;
    type: PortfolioKpiExpandedViewType;
    chartCardType?: 'cycle' | 'summary';
    isLoading?: boolean;
    timeCycleChartData?: KpiTimeCycleChartData;
    columnTitleMappings?: Record<string, string>;
    kpiFiltersApplied?: Record<string, string[]>;
    totalKpiFiltersApplied?: number;
    getMilestoneStatusSummaryData?: () => MilestoneStatusSummaryItem[];
    getTimeCycleChartData?: () => KpiTimeCycleChartData;
    getIsLoading?: () => boolean;
    getKpiFiltersApplied?: () => Record<string, string[]>;
    getTotalKpiFiltersApplied?: () => number;
    onSummaryStatusClick?: (status: string) => void;
    onKpiRemoveFilter?: (payload: KpiRemoveFilterPayload) => void;
    onKpiClearAllFilters?: () => void;
    milestoneStatusSummaryData?: MilestoneStatusSummaryItem[];
  } = inject(MAT_DIALOG_DATA);
  milestoneStatusSummaryData: MilestoneStatusSummaryItem[] = [];
  timeCycleChartData: KpiTimeCycleChartData = DEFAULT_TIME_CYCLE_CHART_DATA;
  isLoading = false;
  kpiFiltersApplied: Record<string, string[]> = {};
  totalKpiFiltersApplied = 0;

  ngOnInit(): void {
    this.refreshExternalState();
    this.startLoadingSyncIfNeeded();
    this.milestoneFilterService.filterState$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refreshExternalStateDeferred();
      this.startLoadingSyncIfNeeded();
    });
  }

  ngOnDestroy(): void {
    this.stopLoadingSync();
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  onSummaryStatusClicked(status: string): void {
    this.data.onSummaryStatusClick?.(status);
    this.refreshExternalStateDeferred();
    this.startLoadingSyncIfNeeded();
  }

  onKpiRemoveFilter(payload: KpiRemoveFilterPayload): void {
    this.data.onKpiRemoveFilter?.(payload);
    this.refreshExternalStateDeferred();
    this.startLoadingSyncIfNeeded();
  }

  onKpiClearAllFilters(): void {
    this.data.onKpiClearAllFilters?.();
    this.refreshExternalStateDeferred();
    this.startLoadingSyncIfNeeded();
  }

  private refreshExternalStateDeferred(): void {
    this.refreshExternalState();
    setTimeout(() => this.refreshExternalState(), 0);
  }

  private refreshExternalState(): void {
    const summaryData =
      this.data.getMilestoneStatusSummaryData?.() ?? this.data.milestoneStatusSummaryData ?? [];
    this.milestoneStatusSummaryData = summaryData.map((item) => ({ ...item }));

    const cycleData = this.data.getTimeCycleChartData?.() ?? this.data.timeCycleChartData;
    this.timeCycleChartData = this.cloneTimeCycleChartData(cycleData);
    this.isLoading = this.data.getIsLoading?.() ?? this.data.isLoading ?? false;

    const filters = this.data.getKpiFiltersApplied?.() ?? this.data.kpiFiltersApplied ?? {};
    this.kpiFiltersApplied = this.cloneFilters(filters);

    const totalFromSource =
      this.data.getTotalKpiFiltersApplied?.() ?? this.data.totalKpiFiltersApplied;
    this.totalKpiFiltersApplied =
      typeof totalFromSource === 'number'
        ? totalFromSource
        : this.countFilters(this.kpiFiltersApplied);
  }

  private cloneFilters(filters: Record<string, string[]>): Record<string, string[]> {
    return Object.entries(filters).reduce(
      (acc, [key, values]) => {
        acc[key] = Array.isArray(values) ? [...values] : [];
        return acc;
      },
      {} as Record<string, string[]>
    );
  }

  private cloneTimeCycleChartData(chartData?: KpiTimeCycleChartData | null): KpiTimeCycleChartData {
    if (!chartData) {
      return {
        ...DEFAULT_TIME_CYCLE_CHART_DATA,
        labels: [...DEFAULT_TIME_CYCLE_CHART_DATA.labels],
        offset: [...DEFAULT_TIME_CYCLE_CHART_DATA.offset],
        timeWeeks: [...DEFAULT_TIME_CYCLE_CHART_DATA.timeWeeks],
        category: [...DEFAULT_TIME_CYCLE_CHART_DATA.category]
      };
    }

    return {
      ...DEFAULT_TIME_CYCLE_CHART_DATA,
      ...chartData,
      labels: Array.isArray(chartData.labels)
        ? chartData.labels.map((label) => ({ ...label }))
        : [],
      offset: Array.isArray(chartData.offset) ? [...chartData.offset] : [],
      timeWeeks: Array.isArray(chartData.timeWeeks) ? [...chartData.timeWeeks] : [],
      category: Array.isArray(chartData.category) ? [...chartData.category] : []
    };
  }

  private countFilters(filters: Record<string, string[]>): number {
    return Object.values(filters).reduce(
      (total, values) => total + (Array.isArray(values) ? values.length : 0),
      0
    );
  }

  private startLoadingSyncIfNeeded(): void {
    if (this.data.type !== 'chart' || !this.data.getIsLoading) {
      return;
    }

    this.stopLoadingSync();
    let hasObservedLoading = this.isLoading;
    let nextDelayMs = this.initialLoadingSyncDelayMs;
    let pendingStartWindowMs = 0;
    const maxPendingStartWindowMs = 2500;

    const scheduleNextSync = () => {
      const delayMs = nextDelayMs;
      this.loadingSyncTimeoutId = setTimeout(() => {
        pendingStartWindowMs += delayMs;
        sync();
      }, delayMs);
      nextDelayMs = Math.min(this.maxLoadingSyncDelayMs, Math.round(nextDelayMs * 1.5));
    };

    const sync = () => {
      this.refreshExternalState();

      if (this.isLoading) {
        hasObservedLoading = true;
      }

      if (!this.isLoading && hasObservedLoading) {
        this.stopLoadingSync();
        return;
      }

      if (!hasObservedLoading && pendingStartWindowMs >= maxPendingStartWindowMs) {
        this.stopLoadingSync();
        return;
      }

      scheduleNextSync();
    };

    scheduleNextSync();
  }

  private stopLoadingSync(): void {
    if (this.loadingSyncTimeoutId === null) {
      return;
    }

    clearTimeout(this.loadingSyncTimeoutId);
    this.loadingSyncTimeoutId = null;
  }
}
